import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField } from "@/lib/crm";
import { fmtDate } from "@/lib/format";
import { Empty, options } from "@/lib/assets";
import { CONTRACT_STATUS, ContractStatus } from "@/lib/contracts";
import { DemoBadge, Panel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ContractDialog } from "@/components/contracts/ContractDialog";

const Kpi = ({ kpi, onPick }) => (
  <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="contracts-kpi">
    {[["in_scadenza", "In scadenza", "text-amber-700"], ["scaduto", "Scaduti", "text-red-600"], ["sospeso", "Sospesi", "text-amber-700"], ["fees_to_check", "Canoni da verificare", "text-brand-700"]].map(([k, l, c]) => (
      <button key={k} type="button" onClick={() => k !== "fees_to_check" && onPick(k)} className="rounded-card border border-line bg-white p-3 text-left transition-colors hover:bg-slate-50" data-testid={`contracts-kpi-${k}`}>
        <p className="text-xs text-slate-500">{l}</p><p className={`font-display text-2xl font-bold ${c}`}>{kpi?.[k] ?? "—"}</p></button>))}
  </div>
);

export const ContractsList = ({ company, showKpi }) => {
  const { can } = useAuth();
  const [flt, setFlt] = useState({ q: "", status: "", expiring: "" });
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [sp, setSp] = useSearchParams();
  const [preset, setPreset] = useState(null);
  useEffect(() => {
    const cid = sp.get("company_id"), oid = sp.get("order_id");
    if (company || sp.get("new") !== "1" || !cid || !can("contracts:create")) return;
    api.get(`/crm/companies/${cid}`).then((r) => { setPreset({ company: { id: cid, name: r.data.company?.name || r.data.name }, orderId: oid }); setOpen(true); }).catch(() => {});
    setSp({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    const p = Object.fromEntries(Object.entries({ q: flt.q, status: flt.status, expiring: flt.expiring ? true : "", company_id: company?.id }).filter(([, v]) => v));
    api.get("/contracts", { params: p }).then((r) => setData(r.data)).catch(() => setData({ items: [], kpi: {} }));
  }, [flt, company?.id]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  return (
    <div data-testid="contracts-list">
      {showKpi && <Kpi kpi={data?.kpi} onPick={(k) => setFlt({ ...flt, status: k })} />}
      <Panel className="p-4 md:p-5">
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca codice, titolo, ordine, offerta" value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} data-testid="contracts-search" /></div>
          <SelectField value={flt.status} onChange={(v) => setFlt({ ...flt, status: v })} options={options(CONTRACT_STATUS)} allowNone="Tutti gli stati" placeholder="Stato" testId="contracts-filter-status" className="w-44" />
          <SelectField value={flt.expiring} onChange={(v) => setFlt({ ...flt, expiring: v })} options={[{ key: "1", label: "In scadenza / scaduti" }]} allowNone="Tutte le scadenze" placeholder="Scadenza" testId="contracts-filter-expiring" className="w-48" />
          {can("contracts:create") && !company?.archived_at && <Button className="gap-1" onClick={() => setOpen(true)} data-testid="contract-new"><Plus className="h-4 w-4" />Nuovo contratto</Button>}
        </div>
        {data && !data.items.length && <Empty testId="contracts-empty">Nessun contratto.</Empty>}
        <ul className="divide-y divide-line">{(data?.items || []).map((c) => (
          <li key={c.id}><Link to={`/contratti/${c.id}`} className="flex flex-wrap items-center gap-3 py-3 hover:bg-slate-50/60" data-testid={`contract-row-${c.id}`}>
            <div className="min-w-0 flex-1"><p className="font-mono text-xs font-semibold text-slate-400">{c.number}{c.origin?.order_number ? ` · ${c.origin.order_number}` : ""}{c.origin?.quote_number ? ` · ${c.origin.quote_number}` : ""}</p>
              <p className="truncate text-sm font-semibold text-ink">{c.title} {c.is_demo && <DemoBadge className="ml-1" />}</p>
              <p className="text-xs text-slate-500">{[!company && c.company_name, c.owner_name, `${c.fees} canoni${c.fees_to_check ? ` (${c.fees_to_check} da verificare)` : ""}`, `${c.includes} voci · ${c.coverage} coperture`].filter(Boolean).join(" · ")}</p></div>
            <span className="text-xs text-slate-500">{fmtDate(c.start_date)} → {c.end_date ? fmtDate(c.end_date) : "indeterminato"}</span>
            <ContractStatus value={c.state} testId={`contract-state-${c.id}`} />
          </Link></li>))}</ul>
      </Panel>
      <ContractDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPreset(null); }} presetCompany={company ? { id: company.id, name: company.name } : preset?.company || null}
        presetOrderId={preset?.orderId} onSaved={load} />
    </div>
  );
};
